import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { logout, getLoggedinUserAction } from '../../redux/actions/userActions';
import { NAV_ITEMS } from '../../config/navItems';
import { getVisibleNavItems, isPortalUser } from '../../utils/rbac';
import useDeploymentScope from '../../hooks/useDeploymentScope';

// T-11 / U-13: portal accounts have no Account Settings, and testers
// searched for a language switcher in the sidebar. Mirrors the pattern
// from MyProfileScreen (i18n + localStorage + PATCH /me/my-profile +
// re-hydrate) so the choice survives refresh.
const PORTAL_LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
];
const LOCALE_LS_KEY = 'regure_locale_settings';


// Map nav item id → tenantConfig.features key. Items missing from this map
// are always visible (e.g. account-settings, my-profile, platform).
const FEATURE_BY_ITEM_ID = {
  // ── Portal (external roles) ──────────────────────────────────────
  // These were absent, and `isItemHidden` treats "not in this map" as
  // "always visible" — so the entire portal ignored TenantConfig feature
  // flags. A Sponsor on a tenant with commissions OFF was offered a My
  // Commissions nav item and a full page whose API answers 403. The
  // backend was right; the frontend was advertising a door that is locked.
  'my-policies':      'policies',
  'my-claims':        'claims',
  'my-clients':       'clients',
  'my-commissions':   'commissions',
  'my-documents':     'documentProcessing',
  // my-profile is deliberately absent: a user's own profile is not a
  // tenant feature and must never be gated away.

  // ── Internal ─────────────────────────────────────────────────────
  dashboard:          'dashboard',
  'claims-analytics': 'claimsAnalytics',
  documents:          'documentProcessing',
  workflows:          'workflowAutomation',
  'risk-signals':     'riskSignals',
  clients:            'clients',
  claims:             'claims',
  policies:           'policies',
  offers:             'offers',
  invoicing:          'invoicing',
  commissions:        'commissions',
  chat:               'chat',
  video:              'video',
  esign:              'eSign',
  audit:              'auditTrail',
  integrity:          'integrityMonitor',
  compliance:         'compliance',
  residency:          'dataResidency',
  dsar:               'dsar',
  'ai-governance':    'aiGovernance',
  team:               'teamManagement',
  brokers:            'brokers',
  carriers:           'riskCarriers',
  bordereaux:         'bordereaux',
  apikeys:            'apiKeys',
  webhooks:           'webhooks',
  integrations:       'integrations',
  // Reinsurance — one flag per sub-module so a tenant can enable
  // Submissions without lighting up Placement/Closings/Portfolio.
  're-overview':      'reinsuranceDashboard',
  're-submissions':   'reinsuranceSubmissions',
  're-placement':     'reinsurancePlacement',
  're-closings':      'reinsuranceClosings',
  're-claims':        'reinsuranceClaimsValidation',
  're-portfolio':     'reinsurancePortfolio',
  // Second wave. A tenant with none of these set — which is every tenant
  // until someone turns them on — renders the same five items it rendered
  // before, because isItemHidden() treats a missing flag as false.
  're-treaties':       'reinsuranceTreaties',
  're-accounting':     'reinsuranceAccounting',
  're-recoveries':     'reinsuranceRecoveries',
  're-counterparties': 'reinsuranceCounterparties',
  're-renewals':       'reinsuranceRenewals',
};

// Resolve item visibility against the tenant config. Falls back to the
// legacy env vars + the static `hidden` flag in NAV_ITEMS when no config
// is loaded so the app keeps rendering during the deploy window.
//
// SaaS God (role === 'admin') bypasses feature-flag hiding entirely.
// Rationale: SaaS God has `org=null` so no tenantConfig ever loads, which
// would otherwise fall through to build-time env vars and hide any feature
// whose LEGACY_ENABLE_* flag isn't set. This caused D2 (Clients nav
// missing for demo@regure.com).
// Compliance-mandatory nav items — RBAC alone gates visibility, tenant
// config cannot hide them. Audit Trail is legally required for anyone
// with audit.read; on tenants where auditTrail is missing/false in the
// features doc, complianceOfficer was losing sidebar access (N1.19).
const COMPLIANCE_MANDATORY_ITEMS = new Set(['audit']);

function isItemHidden(item, tenantConfig, role) {
  if (role === 'admin') return Boolean(item.hidden === true);
  if (COMPLIANCE_MANDATORY_ITEMS.has(item.id)) return Boolean(item.hidden);
  const featureKey = FEATURE_BY_ITEM_ID[item.id];
  if (tenantConfig?.features && featureKey) {
    return tenantConfig.features[featureKey] !== true;
  }
  return Boolean(item.hidden);
}

// Nav structure now lives in config/navItems.js so the login redirect can
// resolve a landing page from the same list the sidebar renders.

// ─── Icons (inline SVG — zero dependencies, matches reference exactly) ───
const Icons = {
  grid: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='3' y='3' width='7' height='7' />
      <rect x='14' y='3' width='7' height='7' />
      <rect x='3' y='14' width='7' height='7' />
      <rect x='14' y='14' width='7' height='7' />
    </svg>
  ),
  'file-text': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
      <polyline points='14 2 14 8 20 8' />
      <line x1='16' y1='13' x2='8' y2='13' />
      <line x1='16' y1='17' x2='8' y2='17' />
      <polyline points='10 9 9 9 8 9' />
    </svg>
  ),
  'git-branch': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <line x1='6' y1='3' x2='6' y2='15' />
      <circle cx='18' cy='6' r='3' />
      <circle cx='6' cy='18' r='3' />
      <path d='M18 9a9 9 0 0 1-9 9' />
    </svg>
  ),
  shield: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
    </svg>
  ),
  'file-check': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
      <polyline points='14 2 14 8 20 8' />
      <path d='M9 15l2 2 4-4' />
    </svg>
  ),
  calculator: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='4' y='2' width='16' height='20' rx='2' />
      <line x1='8' y1='6' x2='16' y2='6' />
      <line x1='8' y1='10' x2='10' y2='10' />
      <line x1='14' y1='10' x2='16' y2='10' />
      <line x1='8' y1='14' x2='10' y2='14' />
      <line x1='14' y1='14' x2='16' y2='14' />
      <line x1='8' y1='18' x2='10' y2='18' />
      <line x1='14' y1='18' x2='16' y2='18' />
    </svg>
  ),
  receipt: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z' />
      <path d='M8 10h8' />
      <path d='M8 14h4' />
    </svg>
  ),
  percent: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <line x1='19' y1='5' x2='5' y2='19' />
      <circle cx='6.5' cy='6.5' r='2.5' />
      <circle cx='17.5' cy='17.5' r='2.5' />
    </svg>
  ),
  'message-circle': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z' />
    </svg>
  ),
  video: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <polygon points='23 7 16 12 23 17 23 7' />
      <rect x='1' y='5' width='15' height='14' rx='2' ry='2' />
    </svg>
  ),
  'pen-tool': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M12 19l7-7 3 3-7 7-3-3z' />
      <path d='M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z' />
      <path d='M2 2l7.586 7.586' />
      <circle cx='11' cy='11' r='2' />
    </svg>
  ),
  lock: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
      <path d='M7 11V7a5 5 0 0 1 10 0v4' />
    </svg>
  ),
  'check-circle': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
      <polyline points='22 4 12 14.01 9 11.01' />
    </svg>
  ),
  globe: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <circle cx='12' cy='12' r='10' />
      <line x1='2' y1='12' x2='22' y2='12' />
      <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
    </svg>
  ),
  'user-check': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
      <circle cx='8.5' cy='7' r='4' />
      <polyline points='17 11 19 13 23 9' />
    </svg>
  ),
  users: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  ),
  briefcase: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='2' y='7' width='20' height='14' rx='2' ry='2' />
      <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
    </svg>
  ),
  building: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='4' y='2' width='16' height='20' />
      <path d='M9 22V12h6v10' />
      <path d='M8 6h.01' />
      <path d='M16 6h.01' />
      <path d='M12 6h.01' />
      <path d='M12 10h.01' />
      <path d='M8 10h.01' />
      <path d='M16 10h.01' />
    </svg>
  ),
  key: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4' />
    </svg>
  ),
  link: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' />
      <path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' />
    </svg>
  ),
  settings: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <circle cx='12' cy='12' r='3' />
      <path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z' />
    </svg>
  ),
  search: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <circle cx='11' cy='11' r='8' />
      <line x1='21' y1='21' x2='16.65' y2='16.65' />
    </svg>
  ),
  chevronRight: (c = 'currentColor', s = 14) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
      <polyline points='9 18 15 12 9 6' />
    </svg>
  ),
  moreVertical: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
      <circle cx='12' cy='5' r='1' />
      <circle cx='12' cy='12' r='1' />
      <circle cx='12' cy='19' r='1' />
    </svg>
  ),
  'log-out': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
      <polyline points='16 17 21 12 16 7' />
      <line x1='21' y1='12' x2='9' y2='12' />
    </svg>
  ),
  cpu: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='4' y='4' width='16' height='16' rx='2' />
      <rect x='9' y='9' width='6' height='6' />
      <line x1='9' y1='1' x2='9' y2='4' />
      <line x1='15' y1='1' x2='15' y2='4' />
      <line x1='9' y1='20' x2='9' y2='23' />
      <line x1='15' y1='20' x2='15' y2='23' />
      <line x1='20' y1='9' x2='23' y2='9' />
      <line x1='20' y1='14' x2='23' y2='14' />
      <line x1='1' y1='9' x2='4' y2='9' />
      <line x1='1' y1='14' x2='4' y2='14' />
    </svg>
  ),
  'bar-chart': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <line x1='12' y1='20' x2='12' y2='10' />
      <line x1='18' y1='20' x2='18' y2='4' />
      <line x1='6' y1='20' x2='6' y2='14' />
      <line x1='3' y1='20' x2='21' y2='20' />
    </svg>
  ),
  'alert-triangle': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
      <line x1='12' y1='9' x2='12' y2='13' />
      <line x1='12' y1='17' x2='12.01' y2='17' />
    </svg>
  ),
  // ── Reinsurance second wave. Stroke weight and 24-box match the set
  //    above so the sidebar rail stays optically even.
  layers: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <polygon points='12 2 2 7 12 12 22 7 12 2' />
      <polyline points='2 17 12 22 22 17' />
      <polyline points='2 12 12 17 22 12' />
    </svg>
  ),
  database: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <ellipse cx='12' cy='5' rx='9' ry='3' />
      <path d='M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5' />
      <path d='M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3' />
    </svg>
  ),
  'refresh-cw': (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <polyline points='23 4 23 10 17 10' />
      <polyline points='1 20 1 14 7 14' />
      <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' />
    </svg>
  ),
  calendar: (c = 'currentColor', s = 18) => (
    <svg width={s} height={s} viewBox='0 0 24 24' fill='none' stroke={c} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
      <rect x='3' y='4' width='18' height='18' rx='2' ry='2' />
      <line x1='16' y1='2' x2='16' y2='6' />
      <line x1='8' y1='2' x2='8' y2='6' />
      <line x1='3' y1='10' x2='21' y2='10' />
    </svg>
  ),
};

const Icon = ({ name, color, size }) => {
  const fn = Icons[name];
  return fn ? fn(color || 'currentColor', size || 18) : null;
};

// ─── Shared color constants ───────────────────────────────
const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
};

// ─── Active route detection ───────────────────────────────
// ─── Sidebar shape, remembered across navigation ──────────────
//
// AppLayout is NOT mounted above the router: every screen renders its own,
// so each nav click unmounts this sidebar and mounts a fresh one. Plain
// useState therefore resets on every navigation, and a section the user had
// collapsed sprang straight back open the moment they clicked anything.
// Nothing was expanding it — the state simply did not survive the click.
//
// localStorage rather than lifting the state up, because this is a per-user
// display preference that should also outlive a reload and a new tab, and
// because moving AppLayout above the router is an app-shell change.
//
// Every access is wrapped: localStorage throws outright in some contexts
// (blocked site data, some private modes), and a sidebar that refuses to
// render because it could not read a preference would be a far worse bug
// than the one being fixed. On any failure the sidebar simply behaves as it
// did before — fully expanded.
const SIDEBAR_PREF_KEY = 'regure.sidebar';

const readSidebarPref = () => {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_PREF_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSidebarPref = (patch) => {
  try {
    window.localStorage.setItem(
      SIDEBAR_PREF_KEY,
      JSON.stringify({ ...readSidebarPref(), ...patch }),
    );
  } catch {
    // Preference not saved. The session still works; it just forgets.
  }
};

const getActiveId = pathname => {
  if (pathname === '/' || pathname === '/secure-files') return 'documents';
  // Handle /orgs/:id detail pages — highlight Platform Overview
  if (/^\/orgs\/[a-f0-9]+$/i.test(pathname)) return 'platform';
  // Handle /portal detail pages — highlight the base portal item
  if (pathname.startsWith('/portal/my-policies')) return 'my-policies';
  if (pathname.startsWith('/portal/my-claims')) return 'my-claims';
  if (pathname.startsWith('/portal/my-clients')) return 'my-clients';
  if (pathname.startsWith('/portal/my-commissions')) return 'my-commissions';
  if (pathname.startsWith('/portal/my-documents')) return 'my-documents';
  if (pathname.startsWith('/portal/my-profile')) return 'my-profile';
  let best = null;
  let bestLen = 0;
  for (const section of NAV_ITEMS) {
    for (const item of section.items) {
      if (item.path === '/') continue;
      const paths = [item.path, ...(item.altPaths || [])];
      for (const p of paths) {
        if (pathname === p || pathname.startsWith(p + '/')) {
          if (p.length > bestLen) {
            best = item.id;
            bestLen = p.length;
          }
        }
      }
    }
  }
  return best;
};

const getInitials = name => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
};

// ═══════════════════════════════════════════════════════════
// REGURE SIDEBAR
// ═══════════════════════════════════════════════════════════
const RegureSidebar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userLogin = useSelector(state => state.userLogin);
  const tenantConfigState = useSelector(state => state.tenantConfig) || {};
  const tenantConfig = tenantConfigState.config;
  const deployScope = useDeploymentScope();
  // Lazy initialisers: the stored preference is read ONCE per mount rather
  // than on every render, and the sidebar's first paint already has the
  // right shape — so a collapsed section never flashes open before a layout
  // effect closes it again.
  const [collapsedSections, setCollapsedSections] = useState(
    () => readSidebarPref().sections || {});
  const [collapsed, setCollapsed] = useState(
    () => Boolean(readSidebarPref().railCollapsed));

  const activeId = getActiveId(location.pathname);

  const toggleSection = (sec) => setCollapsedSections((prev) => {
    const next = { ...prev, [sec]: !prev[sec] };
    writeSidebarPref({ sections: next });
    return next;
  });

  // The whole-rail collapse had exactly the same defect for exactly the same
  // reason: collapse the rail, click any nav item, and it was 272px wide
  // again. Fixed here rather than left half-done, since a remembered section
  // inside a rail that forgets itself is not a fix anyone would call one.
  const toggleRail = () => setCollapsed((prev) => {
    writeSidebarPref({ railCollapsed: !prev });
    return !prev;
  });

  // Try Redux first, fall back to localStorage
  const userData =
    loggedinUser?.userInfo?.data?.data ||
    userLogin?.userInfo?.data?.user ||
    (typeof localStorage !== 'undefined' && localStorage.userInfo ? JSON.parse(localStorage.userInfo)?.data?.user : null);
  const userName = userData?.name || 'User';
  const initials = getInitials(userName);
  // Default fallback previously was 'adjuster' — but adjuster is a real
  // role with actual permissions (documents.upload, claims.read, etc.),
  // so a null userData falling back to 'adjuster' could grant items the
  // real user shouldn't see. Fail-CLOSED with null instead — the parent
  // `userData ? ... : []` gate hides the whole sidebar in that case,
  // matching the login screen's expectations.
  const userRole = userData?.role || null;

  // Hydration gate — render nothing until BOTH user permissions AND the
  // tenant feature config are resolved. Otherwise the sidebar renders the
  // full nav on first paint (tenantConfig null → isItemHidden falls back
  // to legacy env vars) then collapses when fetchTenantConfig resolves,
  // briefly leaking items the user's role/tenant shouldn't see.
  // SaaS God (role === 'admin') has org=null so tenantConfig never loads —
  // skip the wait for them.
  const isTenantConfigResolved =
    userRole === 'admin' ||
    tenantConfigState.config !== null ||
    tenantConfigState.error !== null;
  // Admin visibility depends on the deploy scope — wait for it so we
  // don't render a full platform-admin sidebar first then collapse it
  // when scope resolves (which would still visually leak platform items
  // to operator1/2 on ch.getregure.com for a beat).
  const isDeployScopeResolved = userRole !== 'admin' || Boolean(deployScope);
  const isHydrated = Boolean(userData) && isTenantConfigResolved && isDeployScopeResolved;

  const visibleSections = (isHydrated ? getVisibleNavItems(userRole, NAV_ITEMS, deployScope) : [])
    .map(s => ({ ...s, items: s.items.filter(i => !isItemHidden(i, tenantConfig, userRole)) }))
    .filter(s => s.items.length > 0);

  // Debug logging
  // console.log('[SIDEBAR] userData:', userData?.name, 'role:', userRole, 'visibleSections:', visibleSections.map(s => s.section));

  const W = collapsed ? 64 : 272;

  return (
    <aside
      style={{
        width: W,
        minWidth: W,
        background: 'rgba(255,255,255,0.97)',
        borderRight: '1px solid rgba(148,163,184,0.2)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        backdropFilter: 'blur(30px)',
        height: '100vh',
        position: 'sticky',
        top: 0,
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Logo + collapse toggle */}
      <div
        style={{
          padding: collapsed ? '22px 0 18px' : '22px 16px 18px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 8,
        }}
      >
        {/* Tenant logo where one has been uploaded, the Regure mark
            otherwise. This is a bounded substitution: the shell, layout and
            every other surface stay Regure's. A tenant that uploads nothing
            — which is every tenant today — sees exactly what it saw before. */}
        {/* Until the config resolves we do not know whose logo this is, and
            `|| '/Logo2.svg'` answered that question with "Regure's" — so a
            branded tenant flashed the platform mark before swapping to its
            own. Hold a same-sized placeholder instead; unresolved branding
            is not unbranded. */}
        {!collapsed && !isTenantConfigResolved && (
          <div
            aria-hidden='true'
            style={{ height: 34, width: 150, flexShrink: 1, minWidth: 0 }}
          />
        )}
        {!collapsed && isTenantConfigResolved && (
          <img
            src={tenantConfig?.branding?.logoUrl || '/Logo2.svg'}
            alt={tenantConfig?.branding?.companyName || tenantConfig?.orgName || 'Regure'}
            onError={e => { if (e.currentTarget.src !== window.location.origin + '/Logo2.svg') e.currentTarget.src = '/Logo2.svg'; }}
            style={{ height: 34, width: 'auto', maxWidth: 150, display: 'block', objectFit: 'contain', flexShrink: 1, minWidth: 0 }}
          />
        )}
        {collapsed && tenantConfig?.branding?.logoCompactUrl && (
          <img
            src={tenantConfig.branding.logoCompactUrl}
            alt={tenantConfig?.branding?.companyName || ''}
            style={{ height: 26, width: 26, objectFit: 'contain', display: 'block' }}
          />
        )}
        <button
          onClick={() => toggleRail()}
          title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          style={{
            width: 28,
            height: 28,
            border: `1px solid ${S.borderLight}`,
            borderRadius: 8,
            background: S.bgCard,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: S.textMuted,
            transition: 'background 0.12s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(128,0,32,0.06)';
            e.currentTarget.style.color = S.burgundy;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = S.bgCard;
            e.currentTarget.style.color = S.textMuted;
          }}
        >
          <svg
            width={13}
            height={13}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.2'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
          >
            <polyline points='15 18 9 12 15 6' />
          </svg>
        </button>
      </div>

      {/* Search — hidden when collapsed */}
      {!collapsed && (
        <div style={{ padding: '0 12px 14px', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
              <Icon name='search' color='#94a3b8' size={14} />
            </div>
            <input
              placeholder={t('common.search')}
              style={{
                width: '100%',
                padding: '8px 10px 8px 32px',
                background: S.bgCard,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 9,
                fontSize: 12.5,
                color: S.textPrimary,
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '0 8px' : '0 8px' }}>
        {visibleSections.map(section => (
          <div key={section.section} style={{ marginBottom: collapsed ? 4 : 18 }}>
            {/* Section header — hidden when collapsed */}
            {!collapsed && (
              <div
                onClick={() => toggleSection(section.section)}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: S.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  padding: '0 10px',
                  marginBottom: 4,
                  cursor: 'pointer',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {t(section.sectionKey) || section.section}
                <span style={{ transform: collapsedSections[section.section] ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.2s', display: 'inline-flex' }}>
                  <Icon name='chevronRight' color='#94a3b8' size={10} />
                </span>
              </div>
            )}

            {/* Nav items */}
            {(!collapsedSections[section.section] || collapsed) &&
              section.items.map(item => {
                const isActive = item.id === activeId;
                return collapsed ? (
                  /* Collapsed: icon-only pill with tooltip */
                  <div
                    key={item.id}
                    onClick={() => history.push(item.path)}
                    title={t(item.labelKey)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      cursor: 'pointer',
                      margin: '2px auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isActive ? 'rgba(128,0,32,0.10)' : 'transparent',
                      color: isActive ? S.brandPrimary : '#94a3b8',
                      transition: 'background 0.12s, color 0.12s',
                      borderLeft: isActive ? `3px solid ${S.brandAccent}` : '3px solid transparent',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'rgba(148,163,184,0.10)';
                        e.currentTarget.style.color = S.textSecondary;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#94a3b8';
                      }
                    }}
                  >
                    <Icon name={item.icon} color='currentColor' size={18} />
                  </div>
                ) : (
                  /* Expanded: full row */
                  <div
                    key={item.id}
                    onClick={() => history.push(item.path)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '8px 10px',
                      borderRadius: 10,
                      cursor: 'pointer',
                      marginBottom: 1,
                      transition: 'background 0.12s',
                      background: isActive ? 'rgba(128,0,32,0.07)' : 'transparent',
                      color: isActive ? S.brandPrimary : S.textSecondary,
                      fontWeight: isActive ? 600 : 500,
                      fontSize: 13,
                      borderLeft: isActive ? `3px solid ${S.brandAccent}` : '3px solid transparent',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) e.currentTarget.style.background = 'rgba(148,163,184,0.08)';
                    }}
                    onMouseLeave={e => {
                      if (!isActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Icon name={item.icon} color={isActive ? S.brandPrimary : '#94a3b8'} size={17} />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(item.labelKey)}</span>
                    {item.badge && (
                      <span
                        style={{
                          background: isActive ? S.burgundy : 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 10,
                          minWidth: 20,
                          textAlign: 'center',
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        ))}
      </nav>

      {/* User profile footer */}
      <div style={{ padding: collapsed ? '12px 8px' : '12px 14px', borderTop: '1px solid rgba(148,163,184,0.15)', flexShrink: 0 }}>
        {collapsed ? (
          /* Collapsed footer: avatar + current-lang chip (portal) + logout */
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div
              title={userName}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'default',
              }}
            >
              {initials}
            </div>
            {isPortalUser(userRole) && (
              <button
                type='button'
                title={t('screens.sidebar.changeLanguage', { defaultValue: 'Change language' })}
                onClick={() => {
                  // Cycle EN → FR → DE → EN — keeps the collapsed
                  // footer to one control while still exposing all
                  // three locales the portal supports.
                  const current = (i18n.language || 'en').slice(0, 2);
                  const codes = PORTAL_LANGUAGES.map(l => l.code);
                  const next = codes[(codes.indexOf(current) + 1) % codes.length];
                  i18n.changeLanguage(next);
                  try {
                    const prior = localStorage.getItem(LOCALE_LS_KEY);
                    const parsed = prior ? JSON.parse(prior) : {};
                    parsed.language = next;
                    localStorage.setItem(LOCALE_LS_KEY, JSON.stringify(parsed));
                  } catch { /* ignore */ }
                  const apiUrl = process.env.REACT_APP_BASE_URL || '';
                  axios.patch(`${apiUrl}/me/my-profile`, { preferences: { language: next } }, { withCredentials: true })
                    .then(() => dispatch(getLoggedinUserAction()))
                    .catch(() => { /* local already applied */ });
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(148,163,184,0.25)',
                  background: 'transparent',
                  color: S.textMuted,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {(i18n.language || 'en').slice(0, 2).toUpperCase()}
              </button>
            )}
            <div
              onClick={() => dispatch(logout())}
              title={t('screens.sidebar.logout')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, color: S.textMuted }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(148,163,184,0.1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon name='log-out' color='currentColor' size={15} />
            </div>
          </div>
        ) : (
          /* Expanded footer: avatar + name/role + language switcher (portal only) + logout */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
                <div style={{ fontSize: 11, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
                  {t('screens.sidebar.activeSession')}
                </div>
              </div>
              <div
                onClick={() => dispatch(logout())}
                title={t('screens.sidebar.logout')}
                style={{ cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: S.textMuted }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = S.textMuted;
                }}
              >
                <Icon name='log-out' color='currentColor' size={16} />
              </div>
            </div>

            {/* T-11 / U-13: language switcher for portal users. Internal
                users change language via Account Settings. */}
            {isPortalUser(userRole) && (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                {PORTAL_LANGUAGES.map(lang => {
                  const active = (i18n.language || 'en').slice(0, 2) === lang.code;
                  return (
                    <button
                      key={lang.code}
                      type='button'
                      onClick={async () => {
                        if (active) return;
                        i18n.changeLanguage(lang.code);
                        try {
                          const prior = localStorage.getItem(LOCALE_LS_KEY);
                          const parsed = prior ? JSON.parse(prior) : {};
                          parsed.language = lang.code;
                          localStorage.setItem(LOCALE_LS_KEY, JSON.stringify(parsed));
                        } catch { /* ignore */ }
                        try {
                          const apiUrl = process.env.REACT_APP_BASE_URL || '';
                          await axios.patch(`${apiUrl}/me/my-profile`, { preferences: { language: lang.code } }, { withCredentials: true });
                          dispatch(getLoggedinUserAction());
                        } catch { /* local switch already applied */ }
                      }}
                      style={{
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: 6,
                        border: `1px solid ${active ? S.burgundy : 'rgba(148,163,184,0.25)'}`,
                        background: active ? S.burgundy : 'transparent',
                        color: active ? '#fff' : S.textMuted,
                        cursor: active ? 'default' : 'pointer',
                      }}
                    >
                      {lang.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default RegureSidebar;
